<script setup lang="ts">
// The room's shared queue: anyone in the room can add (from libraries they can see),
// reorder, remove, or jump to an item. Edits are ordinary party updates, so everyone's
// queue follows; what's playing keeps playing while the rest changes.
import { computed, ref, shallowRef, watch } from 'vue'
import type { PartyMedia } from '../api/types'
import { entryRef, playableBlob } from '../lib/libraryPlay'
import { loadLibraryIndex, searchIndex, type FileHit, type LibraryIndex } from '../lib/librarySearch'
import { addToQueue, mediaTitle, moveInQueue, removeFromQueue, type QueueState } from '../lib/party'
import { useParty } from '../stores/party'
import { useSession } from '../stores/session'

const party = useParty()
const session = useSession()
const state = computed<QueueState | null>(() =>
  party.active ? { queue: party.active.party.queue, index: party.active.party.index } : null,
)

function apply(next: QueueState | null, jump = false): void {
  if (!next) return
  void party.update(jump ? { ...next, position_ms: 0, playing: true } : next)
}

// --- Adding: search the libraries I can see, on this device (./librarySearch.ts) ------
const adding = ref(false)
const query = ref('')
const index = shallowRef<LibraryIndex | null>(null)

watch(adding, (on) => {
  if (on && !index.value)
    void loadLibraryIndex(session, (i) => (index.value = { ...i })).catch(() => undefined)
})

/** Playable audio and video only; the first 50 when nothing is typed. */
const results = computed(() =>
  (index.value ? searchIndex(index.value, query.value, true) : [])
    .filter((h) => (h.kind === 'audio' || h.kind === 'video') && playableBlob(h.entry))
    .slice(0, 50),
)

const asMedia = (h: FileHit): PartyMedia => ({ source: 'library', item: entryRef(h.library, h.entry) })

function add(h: FileHit, next: boolean): void {
  if (state.value) apply(addToQueue(state.value, asMedia(h), next))
  session.toast(next ? `${h.title} plays next` : `Added ${h.title}`)
}
</script>

<template>
  <div v-if="state" class="flex max-h-[50vh] flex-col" data-testid="party-queue">
    <div class="flex items-center justify-between border-b border-border px-3 py-2">
      <span class="label mb-0">Queue · {{ String(state.queue.length).padStart(2, '0') }}</span>
      <button class="mono text-text-muted uppercase hover:text-text-primary" data-testid="queue-add" @click="adding = !adding">
        {{ adding ? 'Done' : '+ Add' }}
      </button>
    </div>

    <!-- Search libraries to add -->
    <div v-if="adding" class="border-b border-border px-3 py-2">
      <input v-model="query" class="input h-8 py-0 text-sm" placeholder="Search your libraries" data-testid="queue-search" />
      <ul class="scroll-thin mt-2 max-h-48 overflow-y-auto">
        <li v-if="!index" class="mono py-1 text-text-muted">Reading your libraries…</li>
        <li v-else-if="!results.length" class="mono py-1 text-text-muted">Nothing found.</li>
        <li v-for="i in results" :key="i.library + i.id" class="flex items-center gap-2 py-1 text-sm" :data-testid="`queue-result-${i.title}`">
          <span class="min-w-0 flex-1 truncate">
            {{ i.title }}<span v-if="i.detail" class="text-text-muted"> · {{ i.detail }}</span>
          </span>
          <button class="mono shrink-0 text-text-muted uppercase hover:text-text-primary" data-testid="queue-play-next" @click="add(i, true)">Next</button>
          <button class="mono shrink-0 text-text-muted uppercase hover:text-text-primary" data-testid="queue-add-end" @click="add(i, false)">Add</button>
        </li>
      </ul>
    </div>

    <ol class="scroll-thin overflow-y-auto">
      <li
        v-for="(m, i) in state.queue"
        :key="`${i}:${mediaTitle(m)}`"
        class="group flex items-center gap-2 px-3 py-1.5 text-sm"
        :class="i === state.index ? 'bg-bg-hover text-text-primary' : 'text-text-secondary'"
        :data-testid="`queue-item-${i}`"
        :data-current="i === state.index || undefined"
      >
        <span class="mono w-5 shrink-0 text-text-disabled">{{ i === state.index ? '▶' : String(i + 1).padStart(2, '0') }}</span>
        <button class="min-w-0 flex-1 truncate text-left hover:text-text-primary" :title="`Play ${mediaTitle(m)} now`" @click="apply({ ...state, index: i }, true)">
          {{ mediaTitle(m) }}
        </button>
        <span class="mono flex shrink-0 gap-1 text-text-muted opacity-0 group-hover:opacity-100 focus-within:opacity-100 pointer-coarse:opacity-100">
          <button :disabled="i === 0" :aria-label="`Move ${mediaTitle(m)} up`" class="px-1 hover:text-text-primary disabled:invisible" data-testid="queue-up" @click="apply(moveInQueue(state, i, i - 1))">↑</button>
          <button
            :disabled="i === state.queue.length - 1"
            :aria-label="`Move ${mediaTitle(m)} down`"
            class="px-1 hover:text-text-primary disabled:invisible"
            data-testid="queue-down"
            @click="apply(moveInQueue(state, i, i + 1))"
          >
            ↓
          </button>
          <button
            :disabled="state.queue.length <= 1"
            :aria-label="`Remove ${mediaTitle(m)}`"
            class="px-1 hover:text-signal disabled:invisible"
            data-testid="queue-remove"
            @click="apply(removeFromQueue(state, i), i === state.index)"
          >
            ✕
          </button>
        </span>
      </li>
    </ol>
  </div>
</template>
